import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import * as signalR from "@microsoft/signalr";
import axios from "axios";
import { parseMessage, INITIAL_RACE_STATE } from "./utils/liveMessageParser";
import {
  LIVE_NEGOTIATE_URL,
  LIVE_TRACK_CONFIG_URL,
  API_KEY_FETCH_RACE_DATA,
  API_RACE_CAR_COUNTS_URL,
} from "./utils/apiUrlList";
import { TRACK_EVENT_SELECTION_KEY } from "./TrackEventSelector";
import { api } from "./utils/apiClientWithCsrf";

const LiveStatusContext = createContext(null);

const readTrackName = () => {
  try {
    const raw = localStorage.getItem(TRACK_EVENT_SELECTION_KEY);
    return raw ? JSON.parse(raw)?.trackName || null : null;
  } catch {
    return null;
  }
};

export function LiveStatusProvider({ children }) {
  const [raceState, setRaceState] = useState(INITIAL_RACE_STATE);
  const [pendingRaceState, setPendingRaceState] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [carRunCount, setCarRunCount] = useState(null);
  const [activated, setActivated] = useState(true);
  const [trackName, setTrackName] = useState(readTrackName);
  // Bumping this re-runs the connect effect, tearing down any dead
  // connection and starting fresh. Used by the visibility / online
  // listeners below and the manual Reconnect button to recover from
  // mobile background suspension.
  const [reconnectToken, setReconnectToken] = useState(0);

  const ensureActive = useCallback(() => setActivated(true), []);
  // Manual Reconnect button: wipe displayed state and the merge refs before
  // bumping the token. Auto-reconnects (visibility/online/focus) preserve
  // state on purpose, but a user tapping Reconnect after a phone wake-up
  // expects a clean slate so fresh messages don't merge with stale ones.
  const forceReconnect = useCallback(() => {
    setRaceState(INITIAL_RACE_STATE);
    setPendingRaceState(null);
    raceStateRef.current = INITIAL_RACE_STATE;
    displayedTimestampRef.current = "-";
    setReconnectToken((n) => n + 1);
  }, []);

  const raceStateRef = useRef(INITIAL_RACE_STATE);
  const displayedTimestampRef = useRef("-");
  const trackNameRef = useRef(trackName);
  const connectionStatusRef = useRef(connectionStatus);

  useEffect(() => {
    trackNameRef.current = trackName;
  }, [trackName]);
  useEffect(() => {
    connectionStatusRef.current = connectionStatus;
  }, [connectionStatus]);

  useEffect(() => {
    const onTrackChanged = () => {
      const newName = readTrackName();
      if (newName !== trackNameRef.current) {
        // Only blow away displayed state when the track itself changes.
        // Reconnects (token bumps) intentionally preserve raceState so
        // the screen doesn't blank during a brief socket refresh.
        setRaceState(INITIAL_RACE_STATE);
        setPendingRaceState(null);
        raceStateRef.current = INITIAL_RACE_STATE;
        displayedTimestampRef.current = "-";
      }
      setTrackName(newName);
    };
    window.addEventListener("track-changed", onTrackChanged);
    return () => window.removeEventListener("track-changed", onTrackChanged);
  }, []);

  // Recover from mobile background suspension and network blips. Mobile
  // browsers kill long-lived sockets while a tab is suspended, but the
  // SignalR client can keep reporting "connected" until it tries to send.
  // We force a fresh connection when:
  //   - the tab returns to visible after being hidden for >10s (zombie risk)
  //   - the browser fires "online" (network just came back)
  //   - focus returns and we already know we're not connected
  // Listeners read status via a ref so they're registered once and not
  // re-bound on every status change.
  useEffect(() => {
    const HIDDEN_THRESHOLD_MS = 10_000;
    let hiddenAt = null;

    const reconnect = () => setReconnectToken((n) => n + 1);

    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        hiddenAt = Date.now();
        return;
      }
      const elapsed = hiddenAt == null ? 0 : Date.now() - hiddenAt;
      hiddenAt = null;
      const status = connectionStatusRef.current;
      if (
        elapsed >= HIDDEN_THRESHOLD_MS ||
        (status !== "connected" && status !== "connecting")
      ) {
        reconnect();
      }
    };

    const onOnline = () => reconnect();

    const onFocus = () => {
      const status = connectionStatusRef.current;
      if (status !== "connected" && status !== "connecting") {
        reconnect();
      }
    };

    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("online", onOnline);
    window.addEventListener("focus", onFocus);
    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("online", onOnline);
      window.removeEventListener("focus", onFocus);
    };
  }, []);

  // useEffect(() => {
  //   const { category, round } = raceState;
  //   const eventId = localStorage.getItem("trackEventSelection") ? JSON.parse(localStorage.getItem("trackEventSelection")).eventId : " ";
  //   if (!category || category === "-" || !round || round === "-") return;
  //   axios
  //     .get(API_RACE_CAR_COUNTS_URL, {
  //       params: { category, round, eventId },
  //       withCredentials: true,
  //     })
  //     .then((res) => setCarRunCount(res.data))
  //     .catch((err) => console.error("[CarRunCount] fetch failed:", err));
  // }, [raceState.category, raceState.round]);

  useEffect(() => {
    if (!activated) return undefined;

    let cancelled = false;
    let connection = null;
    const leftLaneRef = { current: null };
    const rightLaneRef = { current: null };

    const setStatus = (s) => {
      if (!cancelled) setConnectionStatus(s);
    };

    async function connect() {
      if (!trackName) {
        setStatus("no-track");
        return;
      }

      setStatus("connecting");

      let hubName, leftLane, rightLane;
      try {
        const { data } = await api.post(
          LIVE_TRACK_CONFIG_URL,
          { trackName },
          { headers: { "X-API-Key": API_KEY_FETCH_RACE_DATA } }
        );
        hubName = data.hubName;
        leftLane = data.leftLane;
        rightLane = data.rightLane;
      } catch (err) {
        console.error("[LiveRace] Track config fetch failed:", err);
        setStatus("disconnected");
        return;
      }
      if (cancelled) return;

      leftLaneRef.current = leftLane;
      rightLaneRef.current = rightLane;

      let url;
      try {
        const { data } = await api.post(LIVE_NEGOTIATE_URL, null, {
          params: { hub:  hubName },
          headers: { "X-API-Key": API_KEY_FETCH_RACE_DATA },
        });
        url = data.url;
      } catch (err) {
        console.error("[LiveRace] SignalR negotiate failed:", err);
        setStatus("disconnected");
        return;
      }
      if (cancelled) return;

      connection = new signalR.HubConnectionBuilder()
        .withUrl(url, {
          // Re-fetch the access token on every (re)connect attempt.
          // Azure SignalR tokens have a ~1 h lifetime; reusing the
          // original token across long reconnect windows causes 401s.
          accessTokenFactory: async () => {
            const { data } = await api.post(LIVE_NEGOTIATE_URL, null, {
              params: { hub: hubName },
              headers: { "X-API-Key": API_KEY_FETCH_RACE_DATA },
            });
            return data.accessToken;
          },
        })
        // Keep retrying forever with a capped backoff. The default policy
        // gives up after 4 attempts (~42 s), which is too short for mobile
        // background suspension. The visibility / online listeners are the
        // backstop if the browser also pauses these timers.
        .withAutomaticReconnect({
          nextRetryDelayInMilliseconds: (ctx) => {
            const schedule = [0, 2000, 5000, 10000];
            return schedule[ctx.previousRetryCount] ?? 30000;
          },
        })
        .configureLogging(signalR.LogLevel.Warning)
        .build();

      connection.on("newMessage", (message) => {
        const next = parseMessage(
          message,
          leftLaneRef.current,
          rightLaneRef.current,
          raceStateRef.current,
          setCarRunCount
        );
        raceStateRef.current = next;
        if (next.raceStarted) {
          displayedTimestampRef.current = next.timestamp;
          setRaceState(next);
          setPendingRaceState(null);
        } else if (
          next.timestamp !== "-" &&
          next.timestamp !== displayedTimestampRef.current
        ) {
          setPendingRaceState(next);
        }
      });

      connection.onreconnecting(() => setStatus("connecting"));
      connection.onreconnected(() => setStatus("connected"));
      connection.onclose(() => setStatus("disconnected"));

      try {
        await connection.start();
        if (cancelled) {
          connection.stop();
          return;
        }
        setStatus("connected");
      } catch (err) {
        console.error("[LiveRace] SignalR connection failed:", err);
        setStatus("disconnected");
      }
    }

    connect();

    return () => {
      // Tear down the socket but preserve displayed state. Reconnects must
      // not blank the screen; track changes clear state via the
      // track-changed listener above before this cleanup runs.
      cancelled = true;
      if (connection) {
        connection.off("newMessage");
        connection.stop();
      }
    };
  }, [activated, trackName, reconnectToken]);

  return (
    <LiveStatusContext.Provider
      value={{
        connectionStatus,
        raceState,
        pendingRaceState,
        carRunCount,
        ensureActive,
        forceReconnect,
      }}
    >
      {children}
    </LiveStatusContext.Provider>
  );
}

export function useLiveStatus() {
  return useContext(LiveStatusContext);
}
